import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Search, Pencil, Trash2, Upload, AlertTriangle, Phone, MapPin, Users as UsersIcon,
  Building2, Landmark, User, X,
} from "lucide-react";
import { toast } from "sonner";
import ConfirmDelete from "@/components/ConfirmDelete";
import { deleteWithUndo } from "@/lib/deleteWithUndo";
import CustomersImportDialog from "@/components/CustomersImportDialog";
import EmptyState from "@/components/EmptyState";
import { SkeletonTable } from "@/components/KovaSkeleton";

import Pagination from "@/components/Pagination";

const CUSTOMER_TYPES = [
  { id: "individual", label: "Individual / Farmer", icon: User, colour: "#166534", badge: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25", dot: "bg-green-500" },
  { id: "business", label: "Business / Organization", icon: Building2, colour: "#D97706", badge: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30", dot: "bg-amber-500" },
  { id: "government", label: "Government / Agency", icon: Landmark, colour: "#991b1b", badge: "bg-red-500/15 text-red-300 border-red-500/25", dot: "bg-red-500" },
];
const CT = Object.fromEntries(CUSTOMER_TYPES.map((t) => [t.id, t]));

const COMM_CHANNELS = [
  { id: "call", label: "Phone Call" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "sms", label: "SMS" },
  { id: "email", label: "Email" },
];

const STATUSES = [
  { id: "active", label: "Active", cls: "bg-[#39FF14]/10 text-[#39FF14]" },
  { id: "inactive", label: "Inactive", cls: "bg-[#1A1F1C] text-[#F8F9FA]" },
  { id: "archived", label: "Archived", cls: "bg-red-500/15 text-red-300" },
];

const emptyForm = {
  customer_type: "individual",
  status: "active",
  farmer_id: "",
  name: "",
  father_or_husband_name: "",
  organization_name: "",
  gst_number: "",
  department_name: "",
  office_division: "",
  contact_person: "",
  designation: "",
  phone: "",
  alternate_phone: "",
  office_phone: "",
  email: "",
  preferred_communication: [],
  village: "",
  gram_panchayat: "",
  post_office: "",
  block: "",
  district: "",
  state: "",
  pin_code: "",
  address: "",
  notes: "",
  custom: {},
};

// UI-level required fields per type
const REQUIRED_UI = {
  individual: ["name", "father_or_husband_name", "phone", "email", "village", "post_office", "block", "district", "state", "pin_code"],
  business: ["organization_name", "contact_person", "designation", "phone", "email", "village", "post_office", "block", "district", "state", "pin_code"],
  government: ["department_name", "office_division", "contact_person", "designation", "phone", "email", "village", "post_office", "block", "district", "state", "pin_code"],
};

const isValidEmail = (e) => !e || (/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(e);
const isValidPhone = (p) => !p || (/^\+?\d[\d\s-]{6,}$/).test(p);

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, complete: 0, incomplete: 0, percentage: 0, by_type: {}, districts: [], states: [], field_labels: {} });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, total_pages: 0, page_size: 25 });
  const [q, setQ] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [incompleteFilter, setIncompleteFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [attempted, setAttempted] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (activeTab !== "all") params.set("customer_type", activeTab);
    if (statusFilter) params.set("status", statusFilter);
    if (districtFilter) params.set("district", districtFilter);
    if (stateFilter) params.set("state", stateFilter);
    if (incompleteFilter) params.set("incomplete", incompleteFilter);
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    // Meta counts use the SAME filter context as the list (except `incomplete`,
    // which is intentionally excluded so the chip can show live counts for
    // all three states — All / Complete / Incomplete — under the active filters).
    const metaParams = new URLSearchParams();
    if (q) metaParams.set("q", q);
    if (activeTab !== "all") metaParams.set("customer_type", activeTab);
    if (statusFilter) metaParams.set("status", statusFilter);
    if (districtFilter) metaParams.set("district", districtFilter);
    if (stateFilter) metaParams.set("state", stateFilter);
    const [r, m] = await Promise.all([
      api.get(`/customers?${params.toString()}`),
      api.get(`/customers/meta?${metaParams.toString()}`),
    ]);
    // Iteration 4 pagination envelope: {items, page, page_size, total, total_pages, has_next, has_prev}
    const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
    setRows(items);
    setPageInfo({
      page: r.data?.page ?? 1,
      page_size: r.data?.page_size ?? pageSize,
      total: r.data?.total ?? items.length,
      total_pages: r.data?.total_pages ?? 1,
    });
    setMeta(m.data);
    setLoading(false);
  };

  // Reset to page 1 when filters/search change (except explicit page navigation).
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line
  }, [q, activeTab, statusFilter, districtFilter, stateFilter, incompleteFilter]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, activeTab, statusFilter, districtFilter, stateFilter, incompleteFilter, page]);

  // Support ?edit=<id> deep-link to open the edit dialog (used by "Complete Profile")
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && rows.length > 0) {
      const target = rows.find((r) => r.id === editId);
      if (target) {
        openEdit(target);
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    }
    /* eslint-disable-next-line */
  }, [rows]);

  // Respect ?incomplete=true|false on first mount (linked from dashboard/quality cards).
  useEffect(() => {
    const inc = searchParams.get("incomplete");
    if (inc === "true" || inc === "false") setIncompleteFilter(inc);
    if (searchParams.get("new") === "1") {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    /* eslint-disable-next-line */
  }, []);

  const required = REQUIRED_UI[form.customer_type] || [];
  const missing = required.filter((k) => !(form[k] || "").toString().trim());

  const openNew = () => { setEditing(null); setForm(emptyForm); setAttempted(false); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...emptyForm, ...r, preferred_communication: r.preferred_communication || [] });
    setAttempted(false);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setAttempted(true);
    if (form.email && !isValidEmail(form.email)) return toast.error("Please enter a valid email address");
    if (form.phone && !isValidPhone(form.phone)) return toast.error("Please enter a valid mobile number");
    // For business/govt, keep `name` mirrored to the org/dept for backward compat
    const payload = { ...form };
    if (form.customer_type === "business" && !payload.name) payload.name = payload.organization_name;
    if (form.customer_type === "government" && !payload.name) payload.name = payload.department_name;
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post(`/customers`, payload);
        toast.success("Customer created");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    }
  };

  const del = async (r) => {
    const displayName = r.name || r.organization_name || r.department_name || r.tzat_no;
    await deleteWithUndo({
      label: displayName,
      request: () => api.delete(`/customers/${r.id}`),
      restore: () => api.post(`/customers/${r.id}/restore`),
      afterMutation: load,
    });
  };

  const clearFilters = () => { setStatusFilter(""); setDistrictFilter(""); setStateFilter(""); setIncompleteFilter(""); };

  const stats = useMemo(() => ({
    all: meta.total_global ?? meta.total,
    individual: meta.by_type?.individual || 0,
    business: meta.by_type?.business || 0,
    government: meta.by_type?.government || 0,
  }), [meta]);

  return (
    <div className="p-8 lg:p-10 max-w-[1500px]">
      <PageHeader
        title="Customers"
        subtitle="Master customer file — the single source of truth every ERP module (Sales, Service, Accounts, Warranty…) connects back to."
        testid="page-customers-header"
        eyebrow="Customer master"
        action={
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-customers-btn" className="bg-transparent border border-[#3A4640] text-[#8A8F98] hover:text-[#F8F9FA] hover:border-[#39FF14]/40 rounded-[10px]">
                <Upload size={14} className="mr-2" /> Import Customers
              </Button>
            )}
            <Button onClick={openNew} data-testid="add-customer-btn" className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px] shadow-[0_0_18px_-4px_rgba(57,255,20,0.4)]">
              <Plus size={14} className="mr-2" /> New Customer
            </Button>
          </div>
        }
      />

      {/* Data quality strip — top row cards stay GLOBAL (whole database snapshot). */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="customer-quality-strip">
        <QualityCard label="Total customers" value={meta.total_global ?? meta.total} tone="neutral" />
        <QualityCard label="Complete profiles" value={meta.complete_global ?? meta.complete} tone="accent" />
        <QualityCard label="Incomplete profiles" value={meta.incomplete_global ?? meta.incomplete} tone="warning" onClick={() => setIncompleteFilter("true")} />
        <QualityCard label="Profile completeness" value={`${meta.percentage_global ?? meta.percentage}%`} tone="neutral" />
      </div>

      {/* Tabs */}
      <div className="border-b border-[#3A4640] mb-4 flex flex-wrap gap-1">
        {["all", "individual", "business", "government"].map((t) => {
          const tabMeta = t === "all"
            ? { label: "All Customers", icon: UsersIcon }
            : CT[t];
          const Icon = tabMeta.icon;
          const isActive = activeTab === t;
          return (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              data-testid={`tab-${t}`}
              className={`px-4 py-2.5 text-sm font-heading font-semibold border-b-2 -mb-px transition-colors ${isActive ? "border-[#39FF14] text-[#F8F9FA]" : "border-transparent text-[#8A8F98] hover:text-[#F8F9FA]"}`}
            >
              <span className="inline-flex items-center gap-2">
                <Icon size={14} className={isActive ? "text-[#39FF14]" : ""} />
                {t === "all" ? "All Customers" : tabMeta.label}
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isActive ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-[#121815] text-[#8A8F98] border border-[#3A4640]"}`}>{stats[t]}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98]" />
          <Input
            data-testid="customer-search"
            className="pl-9 bg-[#121815] border-[#3A4640] text-[#F8F9FA] placeholder:text-[#8A8F98] rounded-[10px] focus-visible:ring-1 focus-visible:ring-[#39FF14]/40 focus-visible:border-[#39FF14]/60"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search TZAT, name, mobile, org, dept, contact, district, state…"
          />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-status" className="w-[150px] bg-[#121815] border-[#3A4640] text-[#F8F9FA] rounded-[10px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={districtFilter || "all"} onValueChange={(v) => setDistrictFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-district" className="w-[170px] bg-[#121815] border-[#3A4640] text-[#F8F9FA] rounded-[10px]"><SelectValue placeholder="District" /></SelectTrigger>
          <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
            <SelectItem value="all">All districts</SelectItem>
            {meta.districts?.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stateFilter || "all"} onValueChange={(v) => setStateFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-state" className="w-[150px] bg-[#121815] border-[#3A4640] text-[#F8F9FA] rounded-[10px]"><SelectValue placeholder="State" /></SelectTrigger>
          <SelectContent className="bg-[#121815] border-[#3A4640] text-[#F8F9FA]">
            <SelectItem value="all">All states</SelectItem>
            {meta.states?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {(statusFilter || districtFilter || stateFilter || incompleteFilter) && (
          <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters" className="text-[#8A8F98] hover:text-[#F8F9FA] hover:bg-[#121815]"><X size={14} className="mr-1" /> Clear filters</Button>
        )}
      </div>

      {/* Profile Status filter — chip toggle with live scoped counts */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="profile-status-filter">
        <span className="text-[10px] uppercase font-heading tracking-[0.15em] text-[#8A8F98] mr-1">Profile Status</span>
        {[
          { id: "",      label: "All",        count: meta.total,      testid: "profile-status-all",        base: "border-[#3A4640] text-[#F8F9FA] bg-[#121815] hover:border-[#39FF14]/40",              active: "border-[#39FF14] text-[#0B0F0C] bg-[#39FF14]" },
          { id: "false", label: "Complete",   count: meta.complete,   testid: "profile-status-complete",   base: "border-[#2ECC71]/30 text-[#39FF14] bg-[#39FF14]/5 hover:bg-[#39FF14]/10",             active: "border-[#39FF14] text-[#0B0F0C] bg-[#39FF14]" },
          { id: "true",  label: "Incomplete", count: meta.incomplete, testid: "profile-status-incomplete", base: "border-yellow-500/30 text-yellow-300 bg-yellow-500/5 hover:bg-yellow-500/10",         active: "border-yellow-400 text-[#0B0F0C] bg-yellow-400" },
        ].map((chip) => {
          const isActive = incompleteFilter === chip.id;
          return (
            <button
              key={chip.testid}
              type="button"
              data-testid={chip.testid}
              onClick={() => setIncompleteFilter(chip.id)}
              className={`text-xs font-heading font-semibold px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-2 ${isActive ? chip.active : chip.base}`}
            >
              {chip.label}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isActive ? "bg-black/20 text-[#0B0F0C]" : "bg-[#0B0F0C]/50 border border-[#3A4640] text-[#F8F9FA]"}`}>
                {chip.count ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <section className="tzat-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#121815] border-b border-[#3A4640] text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading">
            <tr>
              <th className="text-left px-4 py-3">TZAT #</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Location</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Customer since</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="customer-tbody">
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-0">
                  <div className="p-4"><SkeletonTable rows={8} cols={7} /></div>
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6">
                  <EmptyState
                    icon={UsersIcon}
                    title="No customers match your filters"
                    description="Try clearing filters, or add your first customer to the master file."
                    action={
                      <Button onClick={openNew} className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px]" data-testid="empty-add-customer-btn">
                        <Plus size={14} className="mr-2" /> New Customer
                      </Button>
                    }
                    testid="customer-empty"
                  />
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const type = CT[r.customer_type] || CT.individual;
              const status = STATUSES.find((s) => s.id === r.status) || STATUSES[0];
              const displayName = r.customer_type === "business"
                ? (r.organization_name || r.name)
                : r.customer_type === "government"
                  ? (r.department_name || r.name)
                  : r.name;
              const sub = r.customer_type === "business"
                ? `${r.contact_person || "-"}${r.designation ? " · " + r.designation : ""}`
                : r.customer_type === "government"
                  ? `${r.office_division || "-"}${r.contact_person ? " · " + r.contact_person : ""}`
                  : (r.father_or_husband_name ? `S/o ${r.father_or_husband_name}` : "");
              return (
                <tr key={r.id} className="border-b border-[#3A4640] last:border-0 hover:bg-[#121815]/60 transition-colors" data-testid={`customer-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[#39FF14]">{r.tzat_no}</td>
                  <td className="px-4 py-3">
                    <Link to={`/customers/${r.id}`} className="font-medium text-[#F8F9FA] hover:text-[#39FF14] transition-colors" data-testid={`customer-name-${r.id}`}>
                      {displayName || "(no name)"}
                    </Link>
                    {sub && <div className="text-xs text-[#8A8F98] mt-0.5">{sub}</div>}
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {r.incomplete && (
                        <span
                          className="text-[10px] uppercase tracking-wider bg-yellow-500/10 text-yellow-300 border border-yellow-500/30 px-1.5 py-0.5 rounded font-heading inline-flex items-center gap-1 cursor-help"
                          data-testid={`incomplete-badge-${r.id}`}
                          title={
                            (r.missing_required || []).length
                              ? "Missing: " + r.missing_required.map((k) => meta.field_labels?.[k] || k).join(", ")
                              : "Profile marked incomplete"
                          }
                        >
                          <AlertTriangle size={10} /> Incomplete
                          {(r.missing_required || []).length > 0 && (
                            <span className="ml-1 font-mono normal-case tracking-normal text-[10px]" data-testid={`missing-count-${r.id}`}>
                              · {r.missing_required.length} missing
                            </span>
                          )}
                        </span>
                      )}
                      {r.opted_out && (
                        <span className="text-[10px] uppercase tracking-wider bg-[#121815]/5 text-[#8A8F98] border border-white/10 px-1.5 py-0.5 rounded font-heading">Opted out</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-heading uppercase tracking-wider px-2 py-1 rounded-full border border-[#3A4640] bg-[#121815] text-[#F8F9FA]" data-testid={`type-badge-${r.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] shadow-[0_0_6px_rgba(57,255,20,0.7)]"></span>
                      {type.label.split(" / ")[0]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.phone && <div className="flex items-center gap-1 text-[#F8F9FA]"><Phone size={11} className="text-[#39FF14]" /> {r.phone}</div>}
                    {r.email && <div className="text-[#8A8F98] mt-0.5">{r.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {(r.village || r.district || r.state) ? (
                      <div className="text-[#F8F9FA] flex items-start gap-1"><MapPin size={11} className="mt-0.5 text-[#39FF14]" />
                        <span>{[r.village, r.district, r.state].filter(Boolean).join(", ")}</span>
                      </div>
                    ) : <span className="text-[#8A8F98]">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge id={r.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-[#8A8F98]">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-[#121815] text-[#8A8F98] hover:text-[#39FF14] rounded mr-1 transition-colors" data-testid={`edit-customer-${r.id}`} title="Edit"><Pencil size={14} /></button>
                    {isAdmin && (
                      <ConfirmDelete
                        itemLabel={displayName || r.tzat_no}
                        entityLabel="customer"
                        onConfirm={() => del(r)}
                        testid={`customer-${r.id}`}
                      >
                        <button className="p-1.5 hover:bg-red-500/10 text-red-400 rounded transition-colors" data-testid={`delete-customer-${r.id}`}><Trash2 size={14} /></button>
                      </ConfirmDelete>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination
          page={pageInfo.page}
          totalPages={pageInfo.total_pages}
          total={pageInfo.total}
          pageSize={pageInfo.page_size}
          onPageChange={setPage}
          loading={loading}
          testid="customers-pagination"
        />
      </section>

      <CustomerFormDialog
        open={open}
        onOpenChange={setOpen}
        form={form}
        setForm={setForm}
        editing={editing}
        submit={submit}
        attempted={attempted}
        required={required}
        missing={missing}
      />
      <CustomersImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={load}
      />
    </div>
  );
}

function QualityCard({ label, value, tone = "neutral", onClick }) {
  const Base = onClick ? "button" : "div";
  const toneMap = {
    neutral: "border-l-[#39FF14]/40",
    accent:  "border-l-[#39FF14]",
    warning: "border-l-yellow-400",
  };
  const valueColor = {
    neutral: "text-[#F8F9FA]",
    accent:  "text-[#39FF14]",
    warning: "text-yellow-300",
  };
  return (
    <Base
      onClick={onClick}
      className={`text-left px-4 py-3 rounded-[12px] border border-[#3A4640] bg-[#121815] ${onClick ? "hover:border-yellow-500/50 cursor-pointer transition-colors" : ""} ${toneMap[tone]}`}
      style={{ borderLeftWidth: 3 }}
      data-testid={`quality-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8F98] font-heading font-medium">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 ${valueColor[tone]}`}>{value}</div>
    </Base>
  );
}

/* Status badge — neon-green for active, amber for inactive, red for archived. */
function StatusBadge({ id }) {
  const map = {
    active:   { label: "Active",   cls: "bg-[#39FF14]/10 text-[#39FF14] border-[#39FF14]/25" },
    inactive: { label: "Inactive", cls: "bg-[#121815]/5 text-[#8A8F98] border-white/10" },
    archived: { label: "Archived", cls: "bg-red-500/10 text-red-300 border-red-500/25" },
  };
  const s = map[id] || map.active;
  return <span className={`inline-flex text-[10px] uppercase tracking-wider font-heading font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
}

function Req({ children, missing }) {
  return (
    <span className={missing ? "text-red-600" : ""}>{children} <span className="text-red-500">*</span></span>
  );
}

function FormRow({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function FormField({ label, required, missing, error, children, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading">
        {required ? <Req missing={missing}>{label}</Req> : label}
      </Label>
      <div data-testid={testid}>{children}</div>
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}

function CustomerFormDialog({ open, onOpenChange, form, setForm, editing, submit, attempted, required, missing }) {
  const type = CT[form.customer_type] || CT.individual;
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const toggleComm = (id) => {
    const list = form.preferred_communication || [];
    set("preferred_communication", list.includes(id) ? list.filter((c) => c !== id) : [...list, id]);
  };
  const emailError = attempted && form.email && !isValidEmail(form.email) ? "Enter a valid email address" : "";
  const phoneError = attempted && form.phone && !isValidPhone(form.phone) ? "Enter a valid mobile number" : "";
  const miss = new Set(attempted ? missing : []);
  const M = (k) => miss.has(k);
  const R = (k) => required.includes(k);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {editing ? `Edit ${editing.tzat_no || "customer"}` : "New Customer"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6">
          {/* Type toggle */}
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading mb-2">Customer Type</div>
            <div className="grid grid-cols-3 gap-2" data-testid="customer-type-toggle">
              {CUSTOMER_TYPES.map((t) => {
                const Icon = t.icon;
                const selected = form.customer_type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => set("customer_type", t.id)}
                    data-testid={`type-btn-${t.id}`}
                    className={`text-left px-4 py-3 rounded-md border transition-all ${selected ? "border-yellow-500/40 bg-yellow-500/10 shadow-sm" : "border-[#3A4640] bg-[#121815] hover:border-[#3A4640]"}`}
                  >
                    <Icon size={16} style={{ color: t.colour }} />
                    <div className="text-sm font-heading font-bold text-[#F8F9FA] mt-1.5">{t.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {editing && (
            <div className="grid grid-cols-2 gap-4 p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">TZAT Customer Number</div>
                <div className="font-mono font-semibold text-[#F8F9FA]">{editing.tzat_no}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#8A8F98] font-heading">Customer Since</div>
                <div className="font-semibold text-[#F8F9FA]">{formatDate(editing.created_at)}</div>
              </div>
            </div>
          )}

          {/* Section: Basic */}
          <SectionTitle title="Basic Information" hint={type.label} />

          {form.customer_type === "individual" && (
            <>
              <FormRow>
                <FormField label="Farmer ID" testid="ff-farmer-id">
                  <Input value={form.farmer_id} onChange={(e) => set("farmer_id", e.target.value)} placeholder="Optional" />
                </FormField>
                <FormField label="Customer Name" required missing={M("name")} testid="ff-name">
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
                </FormField>
              </FormRow>
              <FormField label="Father's / Husband's Name" required missing={M("father_or_husband_name")} testid="ff-father">
                <Input value={form.father_or_husband_name} onChange={(e) => set("father_or_husband_name", e.target.value)} />
              </FormField>
            </>
          )}

          {form.customer_type === "business" && (
            <FormRow>
              <FormField label="Organization Name" required missing={M("organization_name")} testid="ff-org">
                <Input value={form.organization_name} onChange={(e) => set("organization_name", e.target.value)} />
              </FormField>
              <FormField label="GST Number" testid="ff-gst">
                <Input value={form.gst_number} onChange={(e) => set("gst_number", e.target.value)} placeholder="Optional" />
              </FormField>
            </FormRow>
          )}

          {form.customer_type === "government" && (
            <FormRow>
              <FormField label="Department Name" required missing={M("department_name")} testid="ff-dept">
                <Input value={form.department_name} onChange={(e) => set("department_name", e.target.value)} />
              </FormField>
              <FormField label="Office / Division Name" required missing={M("office_division")} testid="ff-division">
                <Input value={form.office_division} onChange={(e) => set("office_division", e.target.value)} />
              </FormField>
            </FormRow>
          )}

          {(form.customer_type === "business" || form.customer_type === "government") && (
            <>
              <SectionTitle title="Primary Contact" />
              <FormRow>
                <FormField label={form.customer_type === "government" ? "Contact Officer" : "Contact Person"} required missing={M("contact_person")} testid="ff-contact-person">
                  <Input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
                </FormField>
                <FormField label="Designation" required missing={M("designation")} testid="ff-designation">
                  <Input value={form.designation} onChange={(e) => set("designation", e.target.value)} />
                </FormField>
              </FormRow>
            </>
          )}

          {/* Contact */}
          <SectionTitle title="Contact Information" />
          <FormRow>
            <FormField label="Mobile Number" required missing={M("phone")} error={phoneError} testid="ff-phone">
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91…" />
            </FormField>
            <FormField label="Alternate Mobile Number" testid="ff-alt-phone">
              <Input value={form.alternate_phone} onChange={(e) => set("alternate_phone", e.target.value)} placeholder="Optional" />
            </FormField>
          </FormRow>
          <FormRow>
            {form.customer_type === "government" && (
              <FormField label="Office Phone" testid="ff-office-phone">
                <Input value={form.office_phone} onChange={(e) => set("office_phone", e.target.value)} placeholder="Optional" />
              </FormField>
            )}
            <FormField label="Email ID" required missing={M("email")} error={emailError} testid="ff-email">
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </FormField>
          </FormRow>

          {/* Preferred communication */}
          <div>
            <div className="text-xs uppercase tracking-wider text-[#8A8F98] font-heading mb-2">Preferred Communication</div>
            <div className="flex flex-wrap gap-2" data-testid="pref-comm">
              {COMM_CHANNELS.map((c) => {
                const selected = (form.preferred_communication || []).includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleComm(c.id)}
                    data-testid={`pref-${c.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${selected ? "bg-[#39FF14] border-[#39FF14]/40 text-[#0B0F0C]" : "bg-[#121815] border-[#3A4640] text-[#F8F9FA] hover:border-stone-400"}`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Address */}
          <SectionTitle title="Address Information" />
          <FormRow>
            <FormField label={form.customer_type === "individual" ? "Village" : "Village / Locality"} required missing={M("village")} testid="ff-village">
              <Input value={form.village} onChange={(e) => set("village", e.target.value)} />
            </FormField>
            {form.customer_type === "individual" && (
              <FormField label="Gram Panchayat" testid="ff-gp">
                <Input value={form.gram_panchayat} onChange={(e) => set("gram_panchayat", e.target.value)} placeholder="Optional" />
              </FormField>
            )}
          </FormRow>
          <FormRow>
            <FormField label="Post Office" required missing={M("post_office")} testid="ff-po">
              <Input value={form.post_office} onChange={(e) => set("post_office", e.target.value)} />
            </FormField>
            <FormField label="Block / Tehsil" required missing={M("block")} testid="ff-block">
              <Input value={form.block} onChange={(e) => set("block", e.target.value)} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="District" required missing={M("district")} testid="ff-district">
              <Input value={form.district} onChange={(e) => set("district", e.target.value)} />
            </FormField>
            <FormField label="State" required missing={M("state")} testid="ff-state">
              <Input value={form.state} onChange={(e) => set("state", e.target.value)} />
            </FormField>
          </FormRow>
          <FormRow>
            <FormField label="PIN Code" required missing={M("pin_code")} testid="ff-pin">
              <Input value={form.pin_code} onChange={(e) => set("pin_code", e.target.value)} placeholder="6 digits" />
            </FormField>
            <FormField label="Customer Status" testid="ff-status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
          </FormRow>

          <FormField label="Notes" testid="ff-notes">
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Optional context…" />
          </FormField>

          {attempted && missing.length > 0 && (
            <div className="text-xs bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded p-3">
              <b>{missing.length}</b> required field{missing.length === 1 ? "" : "s"} still missing. Fill them or save the record and complete later — an <b>Incomplete Profile</b> tag will be added automatically.
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="customer-submit-btn" className="bg-[#39FF14] hover:bg-[#2ECC71]">
              {editing ? "Save changes" : "Create customer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SectionTitle({ title, hint }) {
  return (
    <div className="border-b border-[#3A4640] pb-2 flex items-baseline gap-3">
      <div className="font-heading font-bold text-[#F8F9FA]">{title}</div>
      {hint && <div className="text-xs text-[#8A8F98]">{hint}</div>}
    </div>
  );
}
